/**
 * "cursors" currently being returned by the API for pagination aren't real cursors. They're base64 encodings of an item's offest. We can manipulate these in the client to do pagination without using any client-side caching by manipulating the Number encoded in the cursor
 * see: https://weightsandbiases.slack.com/archives/C010Y174QGH/p1694209059317359
 */
type PseudoCursor = `arrayconnection:${number}`;

export function isValidCursor(cursor: string): cursor is PseudoCursor {
  const splitCursor = cursor.split(':');
  const offset = Number(splitCursor[1]);
  return [
    splitCursor.length === 2, // the string only contains one ":"
    splitCursor[0] === 'arrayconnection',
    isNaN(offset) !== true,
    offset > -1 && offset < Number.MAX_SAFE_INTEGER,
  ].every(condition => !!condition);
}

export function decodeCursor(c: string): PseudoCursor {
  const cursor = atob(c);
  const isValid = isValidCursor(cursor);

  if (isValid) {
    return cursor;
  }

  throw new Error(`Could not decode pagination cursor: ${c}`);
}

export function encodeCursor(cursor: PseudoCursor): string {
  return btoa(cursor);
}

/**
 * Offset the cursor value by a given offset. Offsets below 0 will be set to 0.
 *
 * @param {string} cursor - The base64 "cursor" value to offset.
 * @param {number} offset - The offset value to apply.
 * @returns {string} The new offset base64 "cursor" value.
 */
export function offsetCursor(cursor: string, offset: number): string {
  const decoded = decodeCursor(cursor);
  const [prefix, o] = decoded.split(':');
  const newOffset = Number(o) + offset;

  // if we ever get back a pagination cursor larger than JS can handle (2^53 - 1)
  // we'll need to rethink this approach. Maybe if we're paginating a list where a customer
  // has indexed all the grains of sand on earth and then expanded to the project to the moon?
  // or there's just something wrong with the API
  if (newOffset > Number.MAX_SAFE_INTEGER) {
    throw new Error('Pagination offset exceeds maximum safe integer');
  }

  if (newOffset < 0) {
    return encodeCursor(`${prefix}:-1` as PseudoCursor);
  }

  return encodeCursor(`${prefix}:${newOffset}` as PseudoCursor);
}
